import 'twin.macro';
import 'styled-components/macro';

import { ApolloError } from '@apollo/client';
import * as React from 'react';
import { LinkedinShareButton, TwitterShareButton } from 'react-share';

import { Button } from 'play/atoms/button/Button';

import { Rating } from '../../../component-lib/atoms/rating/Rating';
import { TrackReviewComplete } from '../../atoms/track-review-complete/TrackReviewComplete';
import { SplashLayout } from '../../templates/splash-layout/SplashLayout';
import { TrackReviewFeedbackForm } from '../track-review-feedback-form/TrackReviewFeedbackForm';
import { ReactComponent as Badge } from './assets/completion-bagde.svg';
import { ReactComponent as LinkedIn } from './assets/linkedin.svg';
import { ReactComponent as Twitter } from './assets/twitter.svg';

const SHARE_URL = `https://${window.location.host}/embed/test-drive/tracks/ansible-tower-demo`;

export interface CompletionScreenProps {
  onFinish: () => void;
  onSubmitScore: (score: number) => void;
  onUpdateReview: (feedback: string) => void;
  feedbackState: 'vote' | 'write' | 'thanks';
  loading: boolean;
  error?: ApolloError;
}

const CompletionScreen = ({
  onFinish,
  onSubmitScore,
  onUpdateReview,
  feedbackState,
  loading,
  error,
}: CompletionScreenProps) => (
  <SplashLayout
    bottomLeft="Powered by Instruqt"
    bottomRight={
      <Button disabled={loading} onClick={onFinish}>
        Finish
      </Button>
    }
  >
    <div tw="flex flex-grow justify-center items-center overflow-auto">
      <div tw="grid grid-cols-2 grid-rows-1 max-w-xl md:max-w-3xl lg:max-w-5xl">
        <div tw="flex flex-col justify-center items-center flex-auto divide-y-1 divide-gray-30 mr-6">
          <div tw="text-center mb-4">
            <h3 tw="display-4">You have successfully completed</h3>
            <h1 tw="display-2">Ansible Tower</h1>
          </div>
          <div tw="pt-4">
            <ReviewSection
              feedbackState={feedbackState}
              onSubmitScore={onSubmitScore}
              onUpdateReview={onUpdateReview}
              loading={loading}
              error={error}
            />
          </div>
        </div>

        <div tw="relative flex flex-auto justify-center items-center bg-gray-5 rounded-xs p-5 ml-6">
          <Badge />
          <div tw="absolute h-6 -mb-3 bottom-0 w-full flex justify-center">
            <div tw="flex justify-center items-center text-gray-70 bg-white rounded-sm shadow-card py-1 px-2">
              <span tw="mr-1 text-xs">Share</span>
              <TwitterShareButton
                url={SHARE_URL}
                hashtags={['instruqt', 'ansibletower', 'badge']}
                related={['Instruqt']}
                title="I successfully completed 'Getting Started with Ansible Tower'"
                tw="mr-1"
              >
                <SocialMediaIcon>
                  <Twitter />
                </SocialMediaIcon>
              </TwitterShareButton>
              {/* LinkedIn has no support for sharing links and text https://stackoverflow.com/a/60559850 */}
              <LinkedinShareButton url={SHARE_URL} source={encodeURIComponent(SHARE_URL)}>
                <SocialMediaIcon>
                  <LinkedIn />
                </SocialMediaIcon>
              </LinkedinShareButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </SplashLayout>
);

const SocialMediaIcon = ({ children, ...props }: { children: React.ReactNode }) => {
  return (
    <div
      tw="flex justify-center items-center h-4 w-4 rounded-sm bg-gray-5 cursor-pointer hover:bg-gray-15 duration-fast"
      {...props}
    >
      {children}
    </div>
  );
};

interface ReviewSectionProps {
  feedbackState: 'vote' | 'write' | 'thanks';
  onSubmitScore: (score: number) => void;
  onUpdateReview: (feedback: string) => void;
  loading: boolean;
  error?: ApolloError;
}

const ReviewSection = ({ feedbackState, onSubmitScore, onUpdateReview, loading, error }: ReviewSectionProps) => {
  switch (feedbackState) {
    case 'vote':
      return (
        <div tw="flex flex-col text-center max-w-xs">
          <h2 tw="display-3 mb-3">How much did you enjoy this track?</h2>
          <Rating onSubmit={onSubmitScore} />
        </div>
      );
    case 'write':
      return <TrackReviewFeedbackForm error={error} loading={loading} onUpdateReview={onUpdateReview} />;
    case 'thanks':
      return <TrackReviewComplete />;
    default:
      return null;
  }
};

export default CompletionScreen;
